import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import {
    FormattedCurrency,
    intlFormatCurrency
} from '@iwIntlProvider/FormattedCurrency';
import { Pill } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './OverviewPanel.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';

const OverviewPanel = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const intl = useIntl();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    if (isLoading || isError) return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;

    const outstandingBalance = account.pending_balance!.total;
    const interest = account.pending_balance!.interest;
    const principal = account.pending_balance!.principal;

    const nextRepaymentAmount = intlFormatCurrency({
        intl,
        value: account.scheduled_payments!.schedule![0].total
    });
    const nextRepaymentDate = intl.formatDate(
        account.scheduled_payments!.schedule![0].date,
        {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }
    );

    const interestOnlyPeriodActive =
        account.is_in_interest_only_repayment_period;

    return (
        <div className={styles.overviewContainer}>
            <div>
                <p className={styles.loanInfoHeading}>
                    <FormattedMessage
                        id="_.outstanding_view.outstandingBalance"
                        defaultMessage="Today's outstanding balance"
                    />
                </p>
                <div className={styles.outstandingBalance}>
                    <FormattedCurrency value={outstandingBalance!} />
                </div>
            </div>

            <div className={styles.loanBreakdownContainer}>
                <div className={styles.currentPrincipal}>
                    <div>
                        <p className={styles.loanInfoHeading}>
                            <FormattedMessage
                                id="_.outstanding_view.currentPrincipal"
                                defaultMessage="Current principal"
                            />
                        </p>
                    </div>
                    <FormattedCurrency
                        value={principal!}
                        className={styles.loanInfo}
                    />
                </div>

                <div>
                    <p className={styles.loanInfoHeading}>
                        <FormattedMessage
                            id="_.outstanding_view.interestAccrued"
                            defaultMessage="Interest"
                        />
                    </p>
                    <FormattedCurrency
                        value={interest!}
                        className={styles.loanInfo}
                    />
                </div>

                <div>
                    <p className={styles.loanInfoHeading}>
                        <FormattedMessage
                            id="_.outstanding_view.nextRepayment"
                            defaultMessage="Next repayment"
                        />
                        {interestOnlyPeriodActive && (
                            <Pill className="ml-s">
                                <FormattedMessage
                                    id="_.outstanding_view.interestOnlyPillName"
                                    defaultMessage="Interest Only"
                                />
                            </Pill>
                        )}
                    </p>
                    <span className={styles.loanInfo}>
                        <FormattedMessage
                            id="_.outstanding_view.nextRepaymentDetail"
                            defaultMessage="{nextRepaymentAmount} on {nextRepaymentDate}"
                            values={{ nextRepaymentAmount, nextRepaymentDate }}
                        />
                    </span>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default OverviewPanel;
